import React, {
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import TimeInput, { TimeInputProps } from '../TimeInput';
import Icon from '../Icon';
import TimePickerUnit from './TimePickerUnit';
import {
  dateToTimeString,
  isTimeEqual,
  TIME_PICKER_FORMATS,
} from '../TimeInput/helpers';
import { getHours, getMinutes, getPeriod, Period } from './helpers';
import Button from '../Button';

const TimePicker = React.forwardRef<HTMLInputElement, TimeInputProps>(
  (
    {
      onFocus,
      value: userValue,
      onChangeTime: userOnChangeTime,
      ...otherProps
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState(false);

    const containerRef = useRef<HTMLDivElement>(null);

    const hoursListRef = useRef<HTMLUListElement>(null);
    const minutesListRef = useRef<HTMLUListElement>(null);
    const periodListRef = useRef<HTMLUListElement>(null);

    const [selectedHours, setSelectedHours] = useState<number | null>(
      getHours(userValue)
    );
    const [selectedMinutes, setSelectedMinutes] = useState<number | null>(
      getMinutes(userValue)
    );
    const [selectedPeriod, setSelectedPeriod] = useState<Period | null>(
      getPeriod(userValue)
    );

    const updateUnits = useCallback(
      (hours, minutes, period) => {
        let time = null;
        if (
          [hours, minutes, period].every((unit) => typeof unit === 'number')
        ) {
          time = userValue ? new Date(userValue) : new Date();
          if (hours === 12) {
            time.setHours(period === Period.AM ? 0 : 12);
          } else {
            time.setHours(period === Period.AM ? hours : hours + 12);
          }
          time.setMinutes(minutes);
        }
        if (!isTimeEqual(time, userValue)) {
          userOnChangeTime(
            time,
            time ? dateToTimeString(time) : null,
            time ? TIME_PICKER_FORMATS[0] : null
          );
        }
        setSelectedHours(hours);
        setSelectedMinutes(minutes);
        setSelectedPeriod(period);
      },
      [userOnChangeTime, userValue]
    );

    useEffect(() => {
      setSelectedHours(getHours(userValue));
      setSelectedMinutes(getMinutes(userValue));
      setSelectedPeriod(getPeriod(userValue));
    }, [userValue]);

    const handleHoursUnitClick = useCallback(
      (hours: number) => {
        updateUnits(hours, selectedMinutes, selectedPeriod);
      },
      [selectedMinutes, selectedPeriod, updateUnits]
    );

    const handleMinutesUnitClick = useCallback(
      (minutes: number) => {
        updateUnits(selectedHours, minutes, selectedPeriod);
      },
      [selectedHours, selectedPeriod, updateUnits]
    );

    const handlePeriodUnitClick = useCallback(
      (period: Period) => {
        updateUnits(selectedHours, selectedMinutes, period);
      },
      [selectedHours, selectedMinutes, updateUnits]
    );

    const handleClickOutside = useCallback((event) => {
      if (containerRef.current && containerRef.current.contains(event.target)) {
        return;
      }
      setIsOpen(false);
    }, []);

    const scrollToSelected = useCallback(
      (listRef: RefObject<HTMLUListElement>) => {
        if (!isOpen || !listRef.current) {
          return;
        }

        const selectedElement = listRef.current.querySelector<HTMLDivElement>(
          '.ui-time-picker-unit-selected'
        );
        if (selectedElement) {
          listRef.current.scrollTo({
            top:
              selectedElement.offsetTop -
              (listRef.current.clientHeight - selectedElement.clientHeight) / 2,
            behavior: 'smooth',
          });
        }
      },
      [isOpen]
    );

    useEffect(() => {
      scrollToSelected(hoursListRef);
    }, [scrollToSelected, selectedHours]);
    useEffect(() => {
      scrollToSelected(minutesListRef);
    }, [scrollToSelected, selectedMinutes]);
    useEffect(() => {
      scrollToSelected(periodListRef);
    }, [scrollToSelected, selectedPeriod]);

    useEffect(() => {
      if (!isOpen) {
        return;
      }

      document.addEventListener('mousedown', handleClickOutside);

      // eslint-disable-next-line consistent-return
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [handleClickOutside, isOpen]);

    const onInputFocus = useCallback(
      (event) => {
        onFocus && onFocus(event);

        if (!event.defaultPrevented) {
          setIsOpen(true);
        }
      },
      [onFocus]
    );

    const handleClickIcon = useCallback(() => {
      setIsOpen(!isOpen);
    }, [isOpen]);

    const { hoursUnits, minutesUnits, periodUnits } = useMemo(() => {
      return {
        hoursUnits: Array.from({ length: 12 }, (_, i) => (
          <TimePickerUnit
            key={i}
            label={`${i + 1}`}
            value={i + 1}
            handleClick={handleHoursUnitClick}
            selected={i + 1 === selectedHours}
          />
        )),
        minutesUnits: Array.from({ length: 60 / 5 }, (_, i) => {
          const val = i * 5;
          return (
            <TimePickerUnit
              key={val}
              label={val < 10 ? `0${val}` : val.toString()}
              value={val}
              handleClick={handleMinutesUnitClick}
              selected={val === selectedMinutes}
            />
          );
        }),
        periodUnits: [
          <TimePickerUnit
            key={Period.AM}
            label="AM"
            value={Period.AM}
            handleClick={handlePeriodUnitClick}
            selected={selectedPeriod === Period.AM}
          />,
          <TimePickerUnit
            key={Period.PM}
            label="PM"
            value={Period.PM}
            handleClick={handlePeriodUnitClick}
            selected={selectedPeriod === Period.PM}
          />,
        ],
      };
    }, [
      handleHoursUnitClick,
      handleMinutesUnitClick,
      handlePeriodUnitClick,
      selectedHours,
      selectedMinutes,
      selectedPeriod,
    ]);
    const onKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Tab') {
        setIsOpen(false);
      }
    };

    return (
      <div ref={containerRef} className="ui-time-picker">
        <TimeInput
          autoComplete="off"
          ref={ref}
          endAdornment={
            <Button
              className="ui-time-picker-trigger"
              tabIndex={-1}
              onClick={handleClickIcon}
              btnStyle="flat"
              size="small"
            >
              <Icon
                className="ui-time-picker-icon"
                name="time-clock"
                size="x-small"
              />
            </Button>
          }
          onFocus={onInputFocus}
          value={userValue}
          onChangeTime={userOnChangeTime}
          {...otherProps}
          onKeyDown={onKeyDown}
        />
        {isOpen && (
          <div className="ui-time-picker-dropdown ui-dropdown">
            <div className="ui-time-picker-units-combo">
              <ul className="ui-time-picker-units" ref={hoursListRef}>
                {hoursUnits}
              </ul>
              <ul className="ui-time-picker-units" ref={minutesListRef}>
                {minutesUnits}
              </ul>
              <ul className="ui-time-picker-units" ref={periodListRef}>
                {periodUnits}
              </ul>
            </div>
          </div>
        )}
      </div>
    );
  }
);

export default TimePicker;
